import { useState, useMemo } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Cell,
  PieChart, Pie,
} from 'recharts'
import { VENDAS_HISTORICO, MESES, ANOS } from '../data/vendas'
import { TrendingUp, TrendingDown, Target, Award, BarChart2, Calendar, Pencil, Check, X, Package, Users as UsersIcon, MapPin, Palette, Wrench, Layers, DollarSign } from 'lucide-react'
import { ImportarBlingHistorico } from '../components/ImportarBlingHistorico'
import { getBlingHistorico } from '../utils/storage'
import { fmtMoeda as fmtFull } from '../utils/fmt'
import { useOrders } from '../hooks/useOrders'
import { CATEGORIAS_MODELO } from '../data/modelos'
import { detectarCidadeAba } from '../data/kanban'
import { getPrecos } from '../utils/precos'

const OVERRIDES_KEY = 'vendas_overrides'

function loadOverrides() {
  try { return JSON.parse(localStorage.getItem(OVERRIDES_KEY) || '{}') } catch { return {} }
}
function saveOverrides(obj) {
  localStorage.setItem(OVERRIDES_KEY, JSON.stringify(obj))
}
function overrideKey(ano, mes) { return `${ano}_${mes}` }

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmt(v) {
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000)     return `R$ ${(v / 1_000).toFixed(0)}k`
  return `R$ ${v.toFixed(0)}`
}
function crescimento(atual, anterior) {
  if (!anterior) return null
  return ((atual - anterior) / anterior) * 100
}

const COR_ANOS = {
  2020: '#94A3B8', 2021: '#60A5FA', 2022: '#34D399',
  2023: '#FBBF24', 2024: '#F97316', 2025: '#A78BFA', 2026: '#F5C200',
}

// ── Tooltip customizado ────────────────────────────────────────────────────────
function TooltipCustom({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-[#111111] rounded-xl px-4 py-3 shadow-xl text-xs space-y-1.5">
      <p className="text-slate-400 font-semibold mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-slate-300">{p.name}:</span>
          <span className="text-white font-bold">{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  )
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, cor = '#F5C200', positivo }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cor}22` }}>
          <Icon size={15} style={{ color: cor }} />
        </div>
      </div>
      <p className="text-2xl font-black text-slate-800 leading-none">{value}</p>
      {sub && (
        <p className={`text-xs font-semibold flex items-center gap-1 ${positivo === true ? 'text-emerald-600' : positivo === false ? 'text-red-500' : 'text-slate-400'}`}>
          {positivo === true && <TrendingUp size={12} />}
          {positivo === false && <TrendingDown size={12} />}
          {sub}
        </p>
      )}
    </div>
  )
}

// Card de ranking horizontal — usado nos top vendedoras / modelos / cidades / cores
function RankingCard({ titulo, icone, itens, corBarra = '#F5C200', vazioMsg = 'Sem dados' }) {
  if (!itens || itens.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
          {icone}
          {titulo}
        </h3>
        <p className="text-xs text-slate-400 italic text-center py-6">{vazioMsg}</p>
      </div>
    )
  }
  const max = Math.max(...itens.map(x => x.valor))
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <h3 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
        {icone}
        {titulo}
      </h3>
      <div className="space-y-2">
        {itens.map((it, i) => {
          const pct = max > 0 ? (it.valor / max) * 100 : 0
          return (
            <div key={i} className="flex items-center gap-2">
              <span className={`w-5 text-center text-[10px] font-black shrink-0
                ${i === 0 ? 'text-[#F5C200]' : i === 1 ? 'text-slate-500' : i === 2 ? 'text-amber-600' : 'text-slate-300'}`}>
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                  <span className="text-xs text-slate-700 font-semibold truncate">{it.label}</span>
                  <span className="text-xs text-slate-800 font-bold shrink-0">{it.fmtValor ? it.fmtValor(it.valor) : it.valor}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: corBarra }} />
                </div>
                {it.extra && (
                  <p className="text-[9px] text-slate-400 mt-0.5">{it.extra}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Fallback de categoria por palavra-chave quando o modelo é texto livre
// (Bling sem catálogo R3) e não bate no mapa CATEGORIAS_MODELO.
function inferirCategoria(nome) {
  const m = (nome || '').toLowerCase()
  if (/mesa|escrivaninha|aparador/.test(m))         return 'Mesas'
  if (/estante|nicho/.test(m))                       return 'Estantes'
  if (/roupeiro/.test(m))                            return 'Roupeiros'
  if (/c[oô]moda/.test(m))                           return 'Cômodas'
  if (/sapateira/.test(m))                           return 'Sapateiras'
  if (/baú|bau/.test(m))                             return 'Baús'
  if (/buffet/.test(m))                              return 'Buffets'
  if (/balc[ãa]o/.test(m))                           return 'Balcões'
  if (/rack|painel.*tv/.test(m))                     return 'Racks/TV'
  if (/cabeceira/.test(m))                           return 'Cabeceiras'
  if (/gaveteiro/.test(m))                           return 'Gaveteiros'
  if (/maleiro/.test(m))                             return 'Maleiros'
  if (/arm[áa]rio/.test(m))                          return 'Armários'
  if (/carrinho/.test(m))                            return 'Carrinhos'
  return 'Outros'
}

// Paleta pra gráficos categóricos
const PALETA_CATEGORIA = [
  '#F5C200', '#F97316', '#10B981', '#60A5FA', '#A78BFA', '#FB923C',
  '#34D399', '#FBBF24', '#F472B6', '#94A3B8', '#22D3EE', '#84CC16',
]

// ── Página ────────────────────────────────────────────────────────────────────
export function Dashboards() {
  // Datas dinâmicas: card preto sempre mostra o mês corrente (hoje);
  // KPI "Jan–X" usa o último mês FECHADO (mês corrente − 1, com fallback em janeiro).
  const hoje         = new Date()
  const anoAtual     = hoje.getFullYear()
  const mesCorrente  = hoje.getMonth() + 1
  const mesAtual     = mesCorrente > 1 ? mesCorrente - 1 : 1
  const [blingKey,  setBlingKey]  = useState(0)
  const [anosVis,   setAnosVis]   = useState([2024, 2025, 2026])
  const [overrides, setOverrides] = useState(loadOverrides)
  const [editMeta,  setEditMeta]  = useState(false)
  const [metaInput, setMetaInput] = useState('')
  const [editMes,   setEditMes]   = useState(false)
  const [mesInput,  setMesInput]  = useState('')
  const [metaAnual, setMetaAnual] = useState(() => {
    try { return Number(localStorage.getItem('meta_anual_2026') || 0) } catch { return 0 }
  })

  // ── Dados por ano ────────────────────────────────────────────────────────
  // VENDAS_HISTORICO (código, fonte oficial) tem prioridade sempre que tem dado.
  // Override de localStorage só vale como fallback pra meses sem dado oficial
  // — tipicamente o mês corrente, que o gestor preenche em tempo real até
  // virar histórico no próximo deploy.
  function valorMes(ano, mes) {
    const historico = VENDAS_HISTORICO.find(v => v.ano === ano && v.mes === mes)?.valor
    if (historico !== undefined) return historico
    const key = overrideKey(ano, mes)
    if (overrides[key] !== undefined) return overrides[key]
    return 0
  }
  function totalAno(ano, ateMes = 12) {
    const meses = [...new Set([
      ...VENDAS_HISTORICO.filter(v => v.ano === ano && v.mes <= ateMes).map(v => v.mes),
      ...Object.keys(overrides).filter(k => k.startsWith(`${ano}_`)).map(k => Number(k.split('_')[1])).filter(m => m <= ateMes),
    ])]
    return meses.reduce((s, m) => s + valorMes(ano, m), 0)
  }

  function salvarMesAtual() {
    const v = Number(mesInput.replace(/\D/g, ''))
    if (v >= 0) {
      const key = overrideKey(anoAtual, mesCorrente)
      const novo = { ...overrides, [key]: v }
      setOverrides(novo)
      saveOverrides(novo)
    }
    setEditMes(false)
  }

  // Para 2026: compara Jan–Abr 2026 vs Jan–Abr 2025 (mesmo período)
  const totalAtual          = totalAno(anoAtual, mesAtual)
  const totalAnteriorPeriodo = totalAno(anoAtual - 1, mesAtual)
  const crescAnual          = crescimento(totalAtual, totalAnteriorPeriodo)

  // Melhor mês histórico
  const melhorMes = useMemo(() => {
    return VENDAS_HISTORICO.reduce((best, v) => v.valor > best.valor ? v : best, VENDAS_HISTORICO[0])
  }, [])

  // Média últimos 12 meses
  const ultimos12 = useMemo(() => {
    const todos = [...VENDAS_HISTORICO].sort((a, b) => a.ano !== b.ano ? a.ano - b.ano : a.mes - b.mes)
    const recent = todos.slice(-12)
    return recent.reduce((s, v) => s + v.valor, 0) / recent.length
  }, [])

  // ── Dados para o gráfico mensal ──────────────────────────────────────────
  const dadosMensais = useMemo(() => MESES.map((nome, i) => {
    const mes = i + 1
    const row = { mes: nome }
    anosVis.forEach(ano => { row[ano] = valorMes(ano, mes) || null })
    if (metaAnual > 0) row.meta = metaAnual / 12
    return row
  }), [anosVis, metaAnual])

  // ── Dados para o gráfico anual ───────────────────────────────────────────
  const dadosAnuais = useMemo(() => ANOS.map(ano => {
    // Para o ano atual usa apenas os meses já realizados; demais anos usam ano completo
    const limite     = ano === anoAtual ? mesAtual : 12
    const limiteAnt  = ano === anoAtual ? mesAtual : 12
    const total      = totalAno(ano, limite)
    const totalAnt   = totalAno(ano - 1, limiteAnt)
    return {
      ano:    ano === anoAtual ? `${ano}*` : String(ano),
      total,
      cresc:  ano > ANOS[0] ? crescimento(total, totalAnt) : null,
      parcial: ano === anoAtual,
    }
  }), [overrides])

  // ── Ranking de meses ─────────────────────────────────────────────────────
  const rankingMeses = useMemo(() => {
    const media = {}
    for (let m = 1; m <= 12; m++) {
      const vals = VENDAS_HISTORICO.filter(v => v.mes === m).map(v => v.valor)
      media[m] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0
    }
    return Object.entries(media)
      .map(([m, v]) => ({ mes: MESES[Number(m) - 1], valor: v }))
      .sort((a, b) => b.valor - a.valor)
  }, [])

  function toggleAno(ano) {
    setAnosVis(prev => prev.includes(ano) ? prev.filter(a => a !== ano) : [...prev, ano].sort())
  }

  function salvarMeta() {
    const v = Number(metaInput.replace(/\D/g, ''))
    if (v > 0) {
      localStorage.setItem('meta_anual_2026', v)
      setMetaAnual(v)
    }
    setEditMeta(false)
  }

  const pctMeta = metaAnual > 0 ? (totalAtual / metaAnual) * 100 : null

  // ── Análises de pedidos (comercial + produto) ──────────────────────────────
  // Vem do useOrders (local + Bling normalizado), excluindo fantasmas (AJ/filhos).
  const { pedidos: pedidosTodos } = useOrders()
  const pedidosReais = useMemo(
    () => pedidosTodos.filter(p => !p.tipoAjuste && !p.tipoFilho),
    [pedidosTodos]
  )
  const pedidosAjuste = useMemo(
    () => pedidosTodos.filter(p => p.tipoAjuste),
    [pedidosTodos]
  )

  // Seletor de período pra seção Comercial — gestor pode navegar entre meses
  // de 2025 e 2026. Default: mês corrente / ano atual.
  const [mesAnalise, setMesAnalise] = useState(mesCorrente)
  const [anoAnalise, setAnoAnalise] = useState(anoAtual)

  // Pedidos do MÊS DE ANÁLISE (data da venda) — pra rankings comerciais focados
  const pedidosMes = useMemo(() => pedidosReais.filter(p => {
    const d = p.dataVenda || (p.criadoEm || '').split('T')[0]
    if (!d) return false
    const [y, m] = String(d).split('-').map(Number)
    return y === anoAnalise && m === mesAnalise
  }), [pedidosReais, anoAnalise, mesAnalise])

  // Top Vendedoras (mês) — soma de valorTotal por nome
  const topVendedoras = useMemo(() => {
    const map = {}
    pedidosMes.forEach(p => {
      const v = (p.vendedora || 'Sem vendedora').trim() || 'Sem vendedora'
      if (!map[v]) map[v] = { nome: v, total: 0, qtd: 0 }
      map[v].total += Number(p.valorTotal || 0)
      map[v].qtd++
    })
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 8)
  }, [pedidosMes])

  // Mapa modelo → categoria (pra agrupar por família)
  const modeloParaCategoria = useMemo(() => {
    const m = {}
    for (const [cat, modelos] of Object.entries(CATEGORIAS_MODELO || {})) {
      for (const nome of modelos) m[nome] = cat
    }
    return m
  }, [])

  // Itera todos os móveis dos pedidos reais. Cada item rende: modelo, cor, categoria
  const itensConsolidados = useMemo(() => {
    const out = []
    pedidosReais.forEach(p => {
      const ms = Array.isArray(p.moveis) ? p.moveis : []
      ms.forEach(m => {
        const modelo = (m?.modelo || '').trim()
        if (!modelo) return
        out.push({
          modelo,
          cor:        (m?.cor || '').trim(),
          categoria:  modeloParaCategoria[modelo] || inferirCategoria(modelo),
          preco:      Number(m?.preco || 0),
        })
      })
    })
    return out
  }, [pedidosReais, modeloParaCategoria])

  // Top 10 modelos mais vendidos (em quantidade)
  const topModelos = useMemo(() => {
    const map = {}
    itensConsolidados.forEach(it => {
      if (!map[it.modelo]) map[it.modelo] = { modelo: it.modelo, qtd: 0, receita: 0 }
      map[it.modelo].qtd++
      map[it.modelo].receita += it.preco
    })
    return Object.values(map).sort((a, b) => b.qtd - a.qtd).slice(0, 10)
  }, [itensConsolidados])

  // Mix por categoria (donut)
  const mixCategorias = useMemo(() => {
    const map = {}
    itensConsolidados.forEach(it => {
      const c = it.categoria || 'Outros'
      if (!map[c]) map[c] = { categoria: c, qtd: 0 }
      map[c].qtd++
    })
    return Object.values(map).sort((a, b) => b.qtd - a.qtd)
  }, [itensConsolidados])

  // Top cores
  const topCores = useMemo(() => {
    const map = {}
    itensConsolidados.forEach(it => {
      const c = it.cor || 'Não informada'
      if (!map[c]) map[c] = { cor: c, qtd: 0 }
      map[c].qtd++
    })
    return Object.values(map).sort((a, b) => b.qtd - a.qtd).slice(0, 10)
  }, [itensConsolidados])

  // Top cidades (mês)
  const topCidades = useMemo(() => {
    const map = {}
    pedidosMes.forEach(p => {
      const c = (p.cliente?.cidade || 'Sem cidade').trim() || 'Sem cidade'
      if (!map[c]) map[c] = { cidade: c, qtd: 0, total: 0 }
      map[c].qtd++
      map[c].total += Number(p.valorTotal || 0)
    })
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 8)
  }, [pedidosMes])

  // Mix por região (todos pedidos)
  const mixRegioes = useMemo(() => {
    const map = { JP: 0, CG: 0, NATAL: 0, RECIFE: 0, OUTRAS: 0 }
    pedidosReais.forEach(p => {
      const r = detectarCidadeAba(p)
      map[r] = (map[r] || 0) + 1
    })
    return [
      { regiao: 'João Pessoa',     qtd: map.JP },
      { regiao: 'Campina Grande',  qtd: map.CG },
      { regiao: 'Natal',           qtd: map.NATAL },
      { regiao: 'Recife',          qtd: map.RECIFE },
      { regiao: 'Outras',          qtd: map.OUTRAS },
    ].filter(x => x.qtd > 0)
  }, [pedidosReais])

  // Indicadores de saúde da operação
  const totalAjustesMes = useMemo(() => pedidosAjuste.filter(p => {
    const iso = p.dataAbertura || p.criadoEm
    if (!iso) return false
    const d = new Date(iso)
    return d.getFullYear() === anoAnalise && (d.getMonth() + 1) === mesAnalise
  }).length, [pedidosAjuste, anoAnalise, mesAnalise])
  const entreguesMes = pedidosMes.filter(p => p.status === 'ENTREGUE').length
  const taxaAjustes = entreguesMes > 0 ? (totalAjustesMes / entreguesMes) * 100 : 0

  // Ticket médio do mês
  const ticketMedioMes = pedidosMes.length > 0
    ? pedidosMes.reduce((s, p) => s + Number(p.valorTotal || 0), 0) / pedidosMes.length
    : 0

  // ── Indicadores estratégicos: Ticket Médio, LTV, Margem de Contribuição ──
  // TICKET MÉDIO e MC respondem ao seletor mês/ano da seção Comercial.
  // LTV é histórico (clientes com ≥2 compras) — não faz sentido recortar por mês.

  // Ticket médio do PERÍODO selecionado (mês/ano da seção Comercial)
  const ticketMedioPeriodo = pedidosMes.length > 0
    ? pedidosMes.reduce((s, p) => s + Number(p.valorTotal || 0), 0) / pedidosMes.length
    : 0

  // LTV médio — agrupa pedidos por cliente (telefone como chave), calcula
  // ticket médio individual, frequência anual estimada, e projeta 3 anos.
  // Considera só clientes com >=2 compras (1 compra não dá pra projetar).
  // É VISÃO HISTÓRICA — não muda com o seletor de mês (LTV é "vida útil").
  const ltvMedio = useMemo(() => {
    const map = {}
    pedidosReais.forEach(p => {
      const tel = (p.cliente?.contato || '').replace(/\D/g, '')
      const key = tel || (p.cliente?.nome || '').toLowerCase()
      if (!key) return
      if (!map[key]) map[key] = { qtd: 0, total: 0, datas: [] }
      map[key].qtd++
      map[key].total += Number(p.valorTotal || 0)
      const d = p.dataVenda || (p.criadoEm || '').split('T')[0]
      if (d) map[key].datas.push(d)
    })
    const clientes = Object.values(map).filter(c => c.qtd >= 2)
    if (clientes.length === 0) return { valor: 0, qtdClientes: 0 }
    const ltvs = clientes.map(c => {
      const sorted = c.datas.sort()
      const ini = new Date(sorted[0] + 'T12:00:00').getTime()
      const fim = new Date(sorted[sorted.length - 1] + 'T12:00:00').getTime()
      const meses = Math.max(1, (fim - ini) / (1000 * 60 * 60 * 24 * 30))
      const ticket    = c.total / c.qtd
      const freqAnual = (c.qtd / meses) * 12
      // Projeção: ticket × freq × 3 anos (horizonte conservador)
      return ticket * freqAnual * 3
    })
    return {
      valor: ltvs.reduce((s, v) => s + v, 0) / ltvs.length,
      qtdClientes: clientes.length,
    }
  }, [pedidosReais])

  // Margem de Contribuição do PERÍODO selecionado (responde ao seletor)
  // ESTIMATIVA via multiplicador padrão da Mini: preço = custo × multiplicador.
  // MC% = 1 - 1/multiplicador. Default 3.5 → 71.4%. É a MC bruta do produto
  // (não inclui mão-de-obra, frete, comissão).
  const margemContribuicao = useMemo(() => {
    const precos = getPrecos()
    const mult = Number(precos.multiplicadorDefault) || 3.5
    const pctMC = (1 - 1 / mult) * 100
    const totalPeriodo = pedidosMes.reduce((s, p) => s + Number(p.valorTotal || 0), 0)
    const mcPeriodo = totalPeriodo * (pctMC / 100)
    return { pct: pctMC, valor: mcPeriodo, multiplicador: mult }
  }, [pedidosMes])

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800">Dashboards</h1>
          <p className="text-sm text-slate-400 mt-1">Indicadores comerciais e de produto · Mini Marcenaria</p>
        </div>
        <div className="flex items-center gap-2">
          <ImportarBlingHistorico onImportado={() => setBlingKey(k => k + 1)} />
          {editMeta ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={metaInput}
                onChange={e => setMetaInput(e.target.value)}
                placeholder="Ex: 3000000"
                className="input w-44 text-sm"
                onKeyDown={e => e.key === 'Enter' && salvarMeta()}
              />
              <button onClick={salvarMeta} className="btn-primary text-sm">Salvar</button>
              <button onClick={() => setEditMeta(false)} className="btn-secondary text-sm">Cancelar</button>
            </div>
          ) : (
            <button
              onClick={() => { setMetaInput(String(metaAnual || '')); setEditMeta(true) }}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <Target size={14} />
              {metaAnual > 0 ? `Meta ${anoAtual}: ${fmt(metaAnual)}` : `Definir meta ${anoAtual}`}
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={BarChart2}
          label={`Jan–${MESES[mesAtual - 1]} ${anoAtual}`}
          value={fmt(totalAtual)}
          sub={crescAnual !== null ? `${crescAnual > 0 ? '+' : ''}${crescAnual.toFixed(1)}% vs mesmo período ${anoAtual - 1}` : undefined}
          positivo={crescAnual !== null ? crescAnual > 0 : undefined}
          cor="#F5C200"
        />
        <KpiCard
          icon={Target}
          label={`Meta ${anoAtual}`}
          value={metaAnual > 0 ? fmt(metaAnual) : '—'}
          sub={pctMeta !== null ? `${pctMeta.toFixed(1)}% atingido` : 'Clique em "Definir meta"'}
          positivo={pctMeta !== null ? pctMeta >= 100 : undefined}
          cor="#10B981"
        />
        <KpiCard
          icon={Award}
          label="Melhor mês histórico"
          value={fmt(melhorMes.valor)}
          sub={`${MESES[melhorMes.mes - 1]} de ${melhorMes.ano}`}
          cor="#F97316"
        />
        <KpiCard
          icon={Calendar}
          label="Média últimos 12 meses"
          value={fmt(ultimos12)}
          sub="Média real"
          cor="#A78BFA"
        />
      </div>

      {/* Indicadores estratégicos: Ticket Médio, LTV e MC.
          Ticket Médio e MC respondem ao seletor de mês/ano da seção Comercial.
          LTV é histórico (vida útil) — não muda com seletor. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={TrendingUp}
          label={`Ticket Médio · ${MESES[mesAnalise - 1]}/${anoAnalise}`}
          value={fmtFull(ticketMedioPeriodo)}
          sub={pedidosMes.length > 0
            ? `${pedidosMes.length} pedido${pedidosMes.length === 1 ? '' : 's'} no período`
            : 'Sem pedidos no período'}
          cor="#06B6D4"
        />
        <KpiCard
          icon={UsersIcon}
          label="LTV médio do cliente"
          value={fmtFull(ltvMedio.valor)}
          sub={ltvMedio.qtdClientes > 0
            ? `Projeção 3 anos · ${ltvMedio.qtdClientes} cliente${ltvMedio.qtdClientes === 1 ? '' : 's'} recorrente${ltvMedio.qtdClientes === 1 ? '' : 's'} (histórico)`
            : 'Aguardando clientes com ≥2 compras'}
          cor="#8B5CF6"
        />
        <KpiCard
          icon={DollarSign}
          label={`Margem · ${MESES[mesAnalise - 1]}/${anoAnalise}`}
          value={`${margemContribuicao.pct.toFixed(1)}%`}
          sub={`${fmtFull(margemContribuicao.valor)} de MC · estimativa via multiplicador ${margemContribuicao.multiplicador.toFixed(2)}x`}
          cor="#10B981"
        />
      </div>

      {/* Card mês atual editável */}
      <div className="bg-[#111111] rounded-2xl p-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">
            Faturamento — {MESES[mesCorrente - 1]} {anoAtual}
          </p>
          {editMes ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[#F5C200] font-bold text-lg">R$</span>
              <input
                autoFocus
                type="text"
                value={mesInput}
                onChange={e => setMesInput(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => { if (e.key === 'Enter') salvarMesAtual(); if (e.key === 'Escape') setEditMes(false) }}
                className="bg-white/10 text-white font-black text-2xl rounded-lg px-3 py-1 w-44 outline-none border-2 border-[#F5C200]"
                placeholder="0"
              />
              <button onClick={salvarMesAtual} className="w-8 h-8 rounded-lg bg-[#F5C200] flex items-center justify-center text-[#111111]">
                <Check size={14} strokeWidth={3} />
              </button>
              <button onClick={() => setEditMes(false)} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-slate-400">
                <X size={14} />
              </button>
            </div>
          ) : (
            <p className="text-3xl font-black text-white">{fmtFull(valorMes(anoAtual, mesCorrente))}</p>
          )}
        </div>
        {!editMes && (
          <button
            onClick={() => { setMesInput(String(Math.round(valorMes(anoAtual, mesCorrente)))); setEditMes(true) }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
          >
            <Pencil size={13} />
            Atualizar
          </button>
        )}
      </div>

      {/* Barra de progresso da meta */}
      {pctMeta !== null && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3 text-sm">
            <span className="font-semibold text-slate-700">Progresso da meta {anoAtual}</span>
            <span className="font-bold text-slate-800">{fmtFull(totalAtual)} <span className="text-slate-400 font-normal">de</span> {fmtFull(metaAnual)}</span>
          </div>
          <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(pctMeta, 100)}%`,
                backgroundColor: pctMeta >= 100 ? '#10B981' : pctMeta >= 70 ? '#F5C200' : '#F97316',
              }}
            />
          </div>
          <div className="flex justify-between mt-1.5 text-xs text-slate-400">
            <span>0%</span>
            <span className={`font-bold ${pctMeta >= 100 ? 'text-emerald-600' : 'text-[#CC8800]'}`}>{pctMeta.toFixed(1)}%</span>
            <span>100%</span>
          </div>
        </div>
      )}

      {/* Gráfico mensal comparativo */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="font-bold text-slate-800">Vendas mensais por ano</h2>
          <div className="flex flex-wrap gap-1.5">
            {ANOS.map(ano => (
              <button
                key={ano}
                onClick={() => toggleAno(ano)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all border-2`}
                style={{
                  borderColor: COR_ANOS[ano],
                  backgroundColor: anosVis.includes(ano) ? COR_ANOS[ano] : 'transparent',
                  color: anosVis.includes(ano) ? '#111' : COR_ANOS[ano],
                }}
              >
                {ano}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={dadosMensais} barGap={2} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={60} />
            <Tooltip content={<TooltipCustom />} />
            {metaAnual > 0 && (
              <ReferenceLine y={metaAnual / 12} stroke="#10B981" strokeDasharray="4 4" label={{ value: 'Meta/mês', fill: '#10B981', fontSize: 10 }} />
            )}
            {anosVis.map(ano => (
              <Bar key={ano} dataKey={ano} name={String(ano)} fill={COR_ANOS[ano]} radius={[3, 3, 0, 0]} maxBarSize={32} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Crescimento anual */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-slate-800">Total anual e crescimento</h2>
          <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
            * {anoAtual} = Jan–{MESES[mesAtual - 1]} vs mesmo período {anoAtual - 1}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={dadosAnuais} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="ano" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={65} />
            <Tooltip content={<TooltipCustom />} />
            <Bar dataKey="total" name="Total anual" radius={[6, 6, 0, 0]} maxBarSize={52}>
              {dadosAnuais.map(d => (
                <Cell key={d.ano} fill={COR_ANOS[Number(d.ano)] || '#94A3B8'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Linha de crescimento % */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wide">Crescimento ano a ano</p>
          <div className="flex flex-wrap gap-2">
            {dadosAnuais.filter(d => d.cresc !== null).map(d => (
              <div key={d.ano} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border
                ${d.cresc >= 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
                {d.cresc >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                {d.ano}: {d.cresc > 0 ? '+' : ''}{d.cresc.toFixed(1)}%
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ COMERCIAL — Indicadores do mês ═══ */}
      <div className="pt-2 border-t-2 border-slate-100">
        <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="text-lg font-black text-slate-800 mb-1 flex items-center gap-2">
              <BarChart2 size={18} className="text-[#F5C200]" />
              Comercial
            </h2>
            <p className="text-xs text-slate-400">
              Indicadores de <strong className="text-slate-600">{MESES[mesAnalise - 1]} / {anoAnalise}</strong>
            </p>
          </div>

          {/* Seletor de período: chips de mês + select de ano */}
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={anoAnalise}
              onChange={e => setAnoAnalise(Number(e.target.value))}
              className="text-xs font-bold rounded-lg border-2 border-slate-200 bg-white px-2 py-1.5 cursor-pointer outline-none hover:border-slate-300"
            >
              <option value={2026}>2026</option>
              <option value={2025}>2025</option>
            </select>
            <div className="flex flex-wrap gap-0.5 bg-slate-100 rounded-lg p-0.5">
              {MESES.map((m, i) => {
                const mesNum = i + 1
                const ativo = mesAnalise === mesNum
                // Bloqueia meses no futuro do ano atual (ainda não aconteceram)
                const futuro = anoAnalise === anoAtual && mesNum > mesCorrente
                return (
                  <button
                    key={m}
                    onClick={() => !futuro && setMesAnalise(mesNum)}
                    disabled={futuro}
                    className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors
                      ${ativo
                        ? 'bg-[#111111] text-[#F5C200] shadow-sm'
                        : futuro
                          ? 'text-slate-300 cursor-not-allowed'
                          : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}
                  >
                    {m}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <KpiCard
            icon={Package}
            label={`Pedidos em ${MESES[mesAnalise - 1]}`}
            value={pedidosMes.length}
            cor="#3B82F6"
          />
          <KpiCard
            icon={TrendingUp}
            label="Ticket médio"
            value={fmtFull(ticketMedioMes)}
            sub="por pedido fechado"
            cor="#10B981"
          />
          <KpiCard
            icon={Wrench}
            label="Ajustes do mês"
            value={totalAjustesMes}
            sub={`${taxaAjustes.toFixed(1)}% sobre entregues`}
            positivo={taxaAjustes < 5 ? true : taxaAjustes > 15 ? false : undefined}
            cor="#F97316"
          />
          <KpiCard
            icon={UsersIcon}
            label="Vendedoras ativas"
            value={topVendedoras.filter(v => v.qtd > 0).length}
            sub="com pelo menos 1 venda"
            cor="#A78BFA"
          />
        </div>

        {/* Top Vendedoras + Top Cidades lado a lado */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankingCard
            titulo={`Top Vendedoras · ${MESES[mesAnalise - 1]}/${anoAnalise}`}
            icone={<UsersIcon size={14} className="text-emerald-600" />}
            itens={topVendedoras.map(v => ({
              label: v.nome,
              valor: v.total,
              extra: `${v.qtd} pedido${v.qtd === 1 ? '' : 's'}`,
              fmtValor: fmtFull,
            }))}
            corBarra="#10B981"
            vazioMsg="Nenhuma venda registrada no período"
          />
          <RankingCard
            titulo={`Top Cidades · ${MESES[mesAnalise - 1]}/${anoAnalise}`}
            icone={<MapPin size={14} className="text-blue-600" />}
            itens={topCidades.map(c => ({
              label: c.cidade,
              valor: c.total,
              extra: `${c.qtd} pedido${c.qtd === 1 ? '' : 's'}`,
              fmtValor: fmtFull,
            }))}
            corBarra="#3B82F6"
            vazioMsg="Sem pedidos com cidade preenchida no período"
          />
        </div>

        {/* Mix de regiões */}
        {mixRegioes.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mt-4">
            <h3 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
              <MapPin size={13} className="text-slate-500" />
              Distribuição por região (todos os pedidos)
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={mixRegioes} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis dataKey="regiao" type="category" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={100} />
                <Tooltip content={<TooltipCustom />} />
                <Bar dataKey="qtd" name="Pedidos" fill="#F5C200" radius={[0, 6, 6, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ═══ PRODUTO — O que está vendendo ═══ */}
      <div className="pt-2 border-t-2 border-slate-100">
        <h2 className="text-lg font-black text-slate-800 mb-1 flex items-center gap-2">
          <Layers size={18} className="text-orange-500" />
          Produto
        </h2>
        <p className="text-xs text-slate-400 mb-4">Mix de modelos, categorias e cores (todos os pedidos)</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Top modelos */}
          <RankingCard
            titulo="Top 10 modelos mais vendidos"
            icone={<Package size={14} className="text-orange-600" />}
            itens={topModelos.map(m => ({
              label: m.modelo,
              valor: m.qtd,
              extra: `${fmt(m.receita)} em vendas`,
              fmtValor: v => `${v} un`,
            }))}
            corBarra="#F97316"
            vazioMsg="Nenhum móvel cadastrado"
          />

          {/* Top cores */}
          <RankingCard
            titulo="Top cores mais usadas"
            icone={<Palette size={14} className="text-amber-600" />}
            itens={topCores.map(c => ({
              label: c.cor,
              valor: c.qtd,
              fmtValor: v => `${v} móveis`,
            }))}
            corBarra="#FBBF24"
            vazioMsg="Sem cores informadas"
          />
        </div>

        {/* Mix por Categoria (donut) */}
        {mixCategorias.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
              <Layers size={13} className="text-slate-500" />
              Mix por categoria de móvel
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={mixCategorias}
                    dataKey="qtd"
                    nameKey="categoria"
                    cx="50%" cy="50%"
                    innerRadius={50}
                    outerRadius={95}
                    paddingAngle={2}
                  >
                    {mixCategorias.map((_, i) => (
                      <Cell key={i} fill={PALETA_CATEGORIA[i % PALETA_CATEGORIA.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<TooltipCustom />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 text-sm">
                {mixCategorias.map((c, i) => {
                  const totalM = mixCategorias.reduce((s, x) => s + x.qtd, 0)
                  const pct = totalM > 0 ? (c.qtd / totalM) * 100 : 0
                  return (
                    <div key={c.categoria} className="flex items-center gap-2 py-1">
                      <span className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: PALETA_CATEGORIA[i % PALETA_CATEGORIA.length] }} />
                      <span className="text-slate-700 flex-1 text-xs">{c.categoria}</span>
                      <span className="font-bold text-slate-800 text-xs">{c.qtd}</span>
                      <span className="text-slate-400 text-[10px] w-10 text-right">{pct.toFixed(1)}%</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ranking de meses */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h2 className="font-bold text-slate-800 mb-4">Ranking de meses (média histórica)</h2>
        <div className="space-y-2">
          {rankingMeses.map((r, i) => {
            const pct = (r.valor / rankingMeses[0].valor) * 100
            return (
              <div key={r.mes} className="flex items-center gap-3">
                <span className={`w-6 text-center text-xs font-black ${i === 0 ? 'text-[#F5C200]' : i === 1 ? 'text-slate-400' : i === 2 ? 'text-amber-600' : 'text-slate-300'}`}>
                  {i + 1}
                </span>
                <span className="w-8 text-xs font-semibold text-slate-600">{r.mes}</span>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${pct}%`, backgroundColor: i < 3 ? '#F5C200' : '#CBD5E1' }}
                  >
                    <span className="text-[10px] font-bold text-[#111111]">{fmt(r.valor)}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}
